/**
 * Leviathan Web - Tank Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import TankForm from '@components/tanks/TankForm';
import { API_URL } from '@/config/api';

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  location: string | null;
  tankType: string;
  capacity: number;
  currentLevel: number;
  minLevel: number | null;
  maxLevel: number | null;
  productId: string | null;
  product: {
    id: string;
    name: string;
    sku: string;
    category: string;
  } | null;
  status: string;
  lastInspectionDate: string | null;
  nextInspectionDue: string | null;
  installDate: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const TankDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [tank, setTank] = useState<Tank | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [levelValue, setLevelValue] = useState('');
  const [levelMode, setLevelMode] = useState<'set' | 'adjust'>('set');
  const [levelError, setLevelError] = useState('');

  const fetchTank = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tanks/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTank(data.tank);
      } else {
        setTank(null);
      }
    } catch (error) {
      console.error('Failed to fetch tank:', error);
      setTank(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchTank();
  }, [fetchTank]);

  const handleDelete = async () => {
    setDeleteError('');
    try {
      const response = await fetch(`${API_URL}/api/tanks/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        navigate('/tanks');
      } else {
        const error = await response.json();
        setDeleteError(error.error || 'Failed to delete tank');
      }
    } catch (error) {
      console.error('Failed to delete tank:', error);
      setDeleteError('Failed to delete tank');
    }
  };

  const handleUpdateLevel = async () => {
    setLevelError('');
    const value = parseFloat(levelValue);

    if (isNaN(value)) {
      setLevelError('Please enter a valid number');
      return;
    }

    try {
      const payload =
        levelMode === 'set' ? { level: value } : { adjustment: value };

      const response = await fetch(`${API_URL}/api/tanks/${id}/level`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowLevelModal(false);
        setLevelValue('');
        fetchTank();
      } else {
        const error = await response.json();
        setLevelError(error.error || 'Failed to update level');
      }
    } catch (error) {
      console.error('Failed to update level:', error);
      setLevelError('Failed to update level');
    }
  };

  const getTankTypeInfo = (type: string): { icon: string; label: string } => {
    switch (type) {
      case 'ABOVE_GROUND':
        return { icon: 'AGT', label: 'Above Ground' };
      case 'UNDERGROUND':
        return { icon: 'UGT', label: 'Underground' };
      case 'PORTABLE':
        return { icon: 'PRT', label: 'Portable' };
      case 'TRAILER':
        return { icon: 'TRL', label: 'Trailer' };
      default:
        return { icon: 'TNK', label: 'Tank' };
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE':
        return 'text-success-green';
      case 'MAINTENANCE':
        return 'text-warning-amber';
      case 'OUT_OF_SERVICE':
        return 'text-error-red';
      case 'DECOMMISSIONED':
        return 'text-text-tertiary';
      default:
        return 'text-text-secondary';
    }
  };

  const getLevelPercent = (tank: Tank): number => {
    // Use maxLevel for percentage calculation (usable capacity), fall back to capacity
    const effectiveMax = tank.maxLevel ?? tank.capacity;
    return effectiveMax > 0 ? (tank.currentLevel / effectiveMax) * 100 : 0;
  };

  const getLevelColor = (percent: number): string => {
    if (percent < 25) return 'bg-error-red';
    if (percent < 50) return 'bg-warning-amber';
    return 'bg-success-green';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading tank data...
        </div>
      </div>
    );
  }

  if (!tank) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-error-red via-steel to-error-red" />
          <div className="border-l-4 border-error-red pl-6">
            <h1 className="text-4xl font-display font-bold text-error-red tracking-wide uppercase">
              Tank Not Found
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <Link
                to="/tanks"
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Back to Tanks
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  if (isEditing) {
    const formatDate = (dateStr: string | null): string => {
      if (!dateStr) return '';
      const parts = dateStr.split('T');
      return parts[0] ?? '';
    };

    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              Edit Tank
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Cancel Editing
              </button>
            </div>
          </div>
        </div>

        <TankForm
          tankId={id!}
          initialData={{
            tankNumber: tank.tankNumber,
            name: tank.name,
            location: tank.location || '',
            tankType: tank.tankType as 'ABOVE_GROUND' | 'UNDERGROUND' | 'PORTABLE' | 'TRAILER',
            capacity: tank.capacity.toString(),
            currentLevel: tank.currentLevel.toString(),
            minLevel: tank.minLevel?.toString() || '',
            maxLevel: tank.maxLevel?.toString() || '',
            productId: tank.productId || '',
            status: tank.status as 'ACTIVE' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'DECOMMISSIONED',
            lastInspectionDate: formatDate(tank.lastInspectionDate),
            nextInspectionDue: formatDate(tank.nextInspectionDue),
            installDate: formatDate(tank.installDate),
            manufacturer: tank.manufacturer || '',
            model: tank.model || '',
            serialNumber: tank.serialNumber || '',
            notes: tank.notes || '',
          }}
          onSuccess={() => {
            setIsEditing(false);
            fetchTank();
          }}
        />
      </div>
    );
  }

  const tankType = getTankTypeInfo(tank.tankType);
  const levelPercent = getLevelPercent(tank);

  // View mode - show details
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {tank.tankNumber}
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/tanks"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Tanks
                </Link>
                <span className="text-text-tertiary text-xs">|</span>
                <span className="text-text-tertiary text-sm font-mono">{tank.name}</span>
                <span className="text-xs font-mono bg-panel-border px-2 py-1 text-steel">
                  {tankType.icon}
                </span>
                <span
                  className={`${getStatusColor(tank.status)} text-xs font-mono uppercase tracking-wider font-semibold`}
                  style={{ textShadow: '0 0 6px currentColor' }}
                >
                  {tank.status.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Link
                to={`/tanks/${id}/history`}
                className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-secondary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
              >
                History
              </Link>
              <button
                onClick={() => setShowLevelModal(true)}
                className="px-6 py-3 bg-amber/20 hover:bg-amber/30 text-amber font-mono uppercase text-sm tracking-wider transition-all border border-amber/30 hover:border-amber"
              >
                Update Level
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-steel/20 hover:bg-steel/30 text-steel font-mono uppercase text-sm tracking-wider transition-all border border-steel/30 hover:border-steel"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 py-3 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono uppercase text-sm tracking-wider transition-all border border-error-red/30 hover:border-error-red"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tank Level Visual */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          <div className="flex items-center space-x-2 mb-6">
            <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
            <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
              Tank Level
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {/* Level Bar */}
            <div className="md:col-span-2">
              <div className="flex justify-between text-sm font-mono mb-2">
                <span className="text-text-tertiary uppercase tracking-widest">Current Level</span>
                <span
                  className="text-amber font-semibold"
                  style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                >
                  {levelPercent.toFixed(1)}%
                </span>
              </div>
              <div className="h-8 bg-background border border-panel-border overflow-hidden">
                <div
                  className={`h-full ${getLevelColor(levelPercent)} transition-all`}
                  style={{ width: `${levelPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-sm font-mono mt-2 text-text-tertiary">
                <span>{tank.currentLevel.toLocaleString()} gallons</span>
                <span>{tank.capacity.toLocaleString()} gallons capacity</span>
              </div>
            </div>

            {/* Level Stats */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-text-tertiary text-xs font-mono uppercase">Available</span>
                <span className="text-text-primary font-mono">
                  {(tank.capacity - tank.currentLevel).toLocaleString()} gal
                </span>
              </div>
              {tank.minLevel && (
                <div className="flex justify-between">
                  <span className="text-text-tertiary text-xs font-mono uppercase">Min Level</span>
                  <span className="text-text-primary font-mono">
                    {tank.minLevel.toLocaleString()} gal
                  </span>
                </div>
              )}
              {tank.maxLevel && (
                <div className="flex justify-between">
                  <span className="text-text-tertiary text-xs font-mono uppercase">Max Level</span>
                  <span className="text-text-primary font-mono">
                    {tank.maxLevel.toLocaleString()} gal
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tank Info */}
        <InfoPanel title="Tank Information">
          <InfoRow label="Tank Number" value={tank.tankNumber} />
          <InfoRow label="Name" value={tank.name} />
          <InfoRow label="Type" value={tankType.label} />
          <InfoRow label="Location" value={tank.location} />
          <InfoRow label="Status" value={tank.status.replace('_', ' ')} />
        </InfoPanel>

        {/* Product Info */}
        <InfoPanel title="Product Information">
          <InfoRow label="Product" value={tank.product?.name} highlight />
          <InfoRow label="SKU" value={tank.product?.sku} />
          <InfoRow label="Category" value={tank.product?.category} />
        </InfoPanel>

        {/* Equipment Details */}
        <InfoPanel title="Equipment Details">
          <InfoRow label="Manufacturer" value={tank.manufacturer} />
          <InfoRow label="Model" value={tank.model} />
          <InfoRow label="Serial Number" value={tank.serialNumber} />
          <InfoRow
            label="Install Date"
            value={tank.installDate ? new Date(tank.installDate).toLocaleDateString() : null}
          />
        </InfoPanel>

        {/* Inspection Info */}
        <InfoPanel title="Inspection & Maintenance">
          <InfoRow
            label="Last Inspection"
            value={
              tank.lastInspectionDate
                ? new Date(tank.lastInspectionDate).toLocaleDateString()
                : null
            }
          />
          <InfoRow
            label="Next Inspection Due"
            value={
              tank.nextInspectionDue
                ? new Date(tank.nextInspectionDue).toLocaleDateString()
                : null
            }
          />
        </InfoPanel>
      </div>

      {/* Notes */}
      {tank.notes && (
        <InfoPanel title="Notes">
          <div className="text-text-primary font-mono text-sm whitespace-pre-wrap">{tank.notes}</div>
        </InfoPanel>
      )}

      {/* Timestamps */}
      <div className="text-text-tertiary text-xs font-mono flex items-center space-x-6">
        <span>Created: {new Date(tank.createdAt).toLocaleString()}</span>
        <span>Updated: {new Date(tank.updatedAt).toLocaleString()}</span>
      </div>

      {/* Update Level Modal */}
      {showLevelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-amber/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-amber mb-4 uppercase">Update Level</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Current level:{' '}
                <strong>
                  {tank.currentLevel.toLocaleString()} / {tank.capacity.toLocaleString()} gallons
                </strong>
              </p>

              {levelError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {levelError}</p>
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Mode
                  </label>
                  <select
                    value={levelMode}
                    onChange={(e) => setLevelMode(e.target.value as 'set' | 'adjust')}
                    className="form-input"
                  >
                    <option value="set">Set Exact Level</option>
                    <option value="adjust">Adjust (+/-)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    {levelMode === 'set' ? 'New Level (gallons)' : 'Adjustment (gallons)'}
                  </label>
                  <input
                    type="number"
                    value={levelValue}
                    onChange={(e) => setLevelValue(e.target.value)}
                    step="0.01"
                    placeholder={levelMode === 'set' ? 'Enter exact level' : 'Positive to add, negative to remove'}
                    className="form-input"
                  />
                  {levelMode === 'adjust' && (
                    <p className="text-text-tertiary text-xs font-mono mt-1">
                      Use negative values to reduce level
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowLevelModal(false);
                    setLevelValue('');
                    setLevelError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLevel}
                  className="px-6 py-3 bg-amber hover:bg-amber/90 text-background font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Update Level
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-error-red mb-4 uppercase">Confirm Delete</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete tank <strong>{tank.tankNumber}</strong>? This action
                cannot be undone.
              </p>

              {deleteError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {deleteError}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-error-red hover:bg-error-red/90 text-white font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Delete Tank
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Info panel with geometric styling
 */
function InfoPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Info row for displaying label/value pairs
 */
function InfoRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | null | undefined;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between items-center border-b border-panel-border pb-3 last:border-0 last:pb-0">
      <span className="text-text-secondary text-xs uppercase tracking-widest font-mono">
        {label}
      </span>
      <span
        className={`font-mono ${highlight ? 'text-amber font-semibold' : 'text-text-primary'}`}
        style={highlight ? { textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' } : undefined}
      >
        {value || <span className="text-text-tertiary">-</span>}
      </span>
    </div>
  );
}

export default TankDetailPage;
